/*
 * Copyright (c) 2025. Bluemate B.V.
 */
import './style/invoiceData.scss';

import {
    ChangeFunction,
    ChangeKeyFunction,
    Loader,
    NumberString,
    preventDefault,
    WithBackground
} from '@dvrd/dvr-controls';

import React, {MouseEventHandler, useContext, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import Invoice, {InvoiceFields} from '../../../../models/invoiceModel';

import {ErrorResetter, InvoiceDataErrors} from '../../../../util/interfaces';

import {BluemateButton, BluemateInput, BluemateNumberInput} from '../../../controls';

import {AppContext} from '../../../misc/appContext';

import CountryField from '../../../controls/country/field/countryField';

interface Props {
    onClose: MouseEventHandler;
    onChange: ChangeKeyFunction<keyof InvoiceFields, string>;
    onChangeInvoiceNumber: ChangeFunction<NumberString>;
    onChangeInvoiceNumberPrefix: ChangeFunction<string>;
    onResetError: ErrorResetter<InvoiceDataErrors>;
    onSubmit: (upload: boolean) => MouseEventHandler;
    invoice: Invoice;
    loading: boolean;
    errors: InvoiceDataErrors;
    active: boolean;
    invoiceNumber: string;
    invoiceNumberPrefix: string;
}

export default function InvoiceData(props: Props) {
    const {t} = useTranslation();
    const {account} = useContext(AppContext).accountContext;
    const {
        onClose,
        onChange,
        onChangeInvoiceNumber,
        onChangeInvoiceNumberPrefix,
        onResetError,
        onSubmit,
        invoice,
        loading,
        errors,
        active,
        invoiceNumber,
        invoiceNumberPrefix
    } = props;
    const lastUsed: string | null = useMemo(() => {
        if (!account) return null;
        return account.getFlex('last_used_invoice_number') ?? null;
    }, [account]);

    function renderBody() {
        return (
            <form className='body-container' onSubmit={preventDefault}>
                <Loader active={loading} />
                {renderExplanation()}
                <div className='divider' />
                {renderInvoiceNumber()}
                <div className='divider' />
                {renderData()}
            </form>
        );
    }

    function renderExplanation() {
        return (
            <div className='invoice-number-container'>
                <label className='part-title'>
                    {t('invoice.request.data.whyManualTitle', 'Waarom moet ik dit handmatig doen?')}
                </label>
                <label className='message'>
                    {t(
                        'invoice.request.data.whyManualBody',
                        'Deze factuur is waarschijnlijk van voor gebruik Bluemate. We krijgen hierbij (vanwege de AVG) waarschijnlijk niet alle klantgegevens. Deze moet je zelf daarom handmatig nog aanvullen. Doordat de Belastingdienst vereist dat jouw factuurnummering chronologisch oploopt, kunnen we hiervoor niet het standaard factuurnummer gebruiken.'
                    )}
                </label>
            </div>
        );
    }

    function renderInvoiceNumber() {
        return (
            <div className='invoice-number-container'>
                <label className='part-title'>{t('invoice.request.data.step1Title', '1) Kies factuurnummer')}</label>
                <label className='message'>
                    {t(
                        'invoice.request.data.step1Body',
                        'Kies een factuurnummer voor deze factuur. Het factuurnummer mag niet eerder gebruikt zijn en de facturen moeten met oplopende factuurnummers genummerd worden. Lees hierboven waarom.'
                    )}
                </label>
                {!!lastUsed && (
                    <label>
                        <b>{t('invoice.request.data.lastFilled', 'Laatste keer vulde je in:')}</b> {lastUsed}
                    </label>
                )}
                <div className='input-row'>
                    <BluemateInput
                        onChange={onChangeInvoiceNumberPrefix}
                        value={invoiceNumberPrefix}
                        label={t('invoice.request.data.prefix', 'Voorvoegsel')}
                        inputProps={{placeholder: t('invoice.request.data.prefixPh', '{jaar}oud-')}}
                        error={errors.invoiceNumberPrefix}
                        onFocus={onResetError('invoiceNumberPrefix')}
                    />
                    <BluemateNumberInput
                        onChange={onChangeInvoiceNumber}
                        value={invoiceNumber}
                        label={t('invoice.request.data.invoiceNumber', 'Factuurnummer')}
                        inputProps={{placeholder: '0001'}}
                        onFocus={onResetError('invoiceNumber')}
                        error={errors.invoiceNumber}
                        wholeNumbers
                    />
                </div>
            </div>
        );
    }

    function renderData() {
        return (
            <div className='invoice-data-container'>
                <label className='part-title'>
                    {t('invoice.request.data.step2Title', '2) Controleer factuurgegevens en vul aan')}
                </label>
                <label className='message'>
                    {t(
                        'invoice.request.data.step2Body',
                        'Deze gegevens zijn ingevuld bij de factuuraanvraag en worden gebruikt voor op de factuur. Je kunt deze nog aanpassen voordat de factuur aangemaakt wordt.'
                    )}
                </label>
                <div className='row'>
                    <BluemateInput
                        onChange={onChange('firstname')}
                        value={invoice.firstname}
                        label={t('invoice.request.data.firstName', 'Voornaam')}
                        placeholder={t('invoice.request.data.firstName', 'Voornaam')}
                        error={errors.firstname}
                        onFocus={onResetError('firstname')}
                    />
                    <BluemateInput
                        onChange={onChange('lastname')}
                        value={invoice.lastname}
                        label={t('invoice.request.data.lastName', 'Achternaam')}
                        placeholder={t('invoice.request.data.lastName', 'Achternaam')}
                        error={errors.lastname}
                        onFocus={onResetError('lastname')}
                    />
                </div>
                <div className='row'>
                    <BluemateInput
                        onChange={onChange('street')}
                        value={invoice.street}
                        label={t('invoice.request.data.street', 'Straat')}
                        placeholder={t('invoice.request.data.street', 'Straat')}
                        error={errors.address}
                        onFocus={onResetError('address')}
                    />
                    <BluemateInput
                        onChange={onChange('house_number')}
                        value={invoice.houseNumber}
                        label={t('invoice.request.data.houseNumber', 'Huisnummer')}
                        placeholder={t('invoice.request.data.houseNumber', 'Huisnummer')}
                        error={errors.houseNumber}
                        onFocus={onResetError('houseNumber')}
                    />
                </div>
                <div className='row'>
                    <BluemateInput
                        onChange={onChange('zip_code')}
                        value={invoice.zipCode}
                        label={t('invoice.request.data.zipCode', 'Postcode')}
                        placeholder={t('invoice.request.data.zipCode', 'Postcode')}
                        error={errors.zipCode}
                        onFocus={onResetError('zipCode')}
                    />
                    <BluemateInput
                        onChange={onChange('city')}
                        value={invoice.city}
                        label={t('invoice.request.data.city', 'Woonplaats')}
                        placeholder={t('invoice.request.data.city', 'Woonplaats')}
                        error={errors.city}
                        onFocus={onResetError('city')}
                    />
                </div>
                <CountryField
                    onChange={onChange('country')}
                    value={invoice.countryName}
                    label={t('invoice.request.data.country', 'Land')}
                    labelAsValue
                />
                <BluemateInput
                    onChange={onChange('company')}
                    value={invoice.company}
                    label={t('invoice.request.data.companyName', 'Bedrijfsnaam')}
                    placeholder={t('invoice.request.data.optional', 'optioneel')}
                />
                <div className='row'>
                    <BluemateInput
                        onChange={onChange('coc_number')}
                        value={invoice.cocNumber}
                        label={t('invoice.request.data.cocNumber', 'KvK-nummer')}
                        placeholder={t('invoice.request.data.optional', 'optioneel')}
                    />
                    <BluemateInput
                        onChange={onChange('vat_number')}
                        value={invoice.vatNumber}
                        label={t('invoice.request.data.vatNumber', 'BTW-nummer')}
                        placeholder={t('invoice.request.data.optional', 'optioneel')}
                    />
                </div>
            </div>
        );
    }

    return (
        <WithBackground active={active} onClose={onClose} preventBackgroundClick>
            <div className='default-popup invoice-request-data'>
                <div className='header-container'>
                    <label className='header-title'>
                        {t('invoice.request.data.popupTitle', 'Handmatig factuur aanmaken voor order')}{' '}
                        {invoice.orderId}
                    </label>
                </div>
                {renderBody()}
                <div className='footer-container'>
                    <BluemateButton
                        onClick={onClose}
                        label={t('common.cancel', 'Annuleren')}
                        disabled={loading}
                        secondary
                    />
                    <div className='submit-buttons'>
                        <BluemateButton
                            onClick={onSubmit(false)}
                            label={t('invoice.request.data.createInvoice', 'Factuur aanmaken')}
                            disabled={loading}
                            secondary
                        />
                        <BluemateButton
                            onClick={onSubmit(true)}
                            label={t('invoice.request.data.createAndUpload', 'Factuur aanmaken & uploaden')}
                            disabled={loading}
                        />
                    </div>
                </div>
            </div>
        </WithBackground>
    );
}
